import { returnPageVariableHash } from './lm_title_description_service';

import config from '@/localconfig';
import { parseUrlForGenderWithString } from './maps';
import { getLocaleFromPath } from './getLocaleFromPath';
import { REGIONS } from '@/utils/regions';

const urlBase = config.BASE_URL;

// Known locale prefixes without leading slash (e.g. 'uk', 'de', 'fr')
const LOCALE_PREFIXES = REGIONS.map((r) => r.prefix.replace(/^\//, '')).filter(Boolean);

/**
 * Strip a locale prefix from a pathname string.
 * e.g. '/de/net-worth/men/24-years-old' → '/net-worth/men/24-years-old'
 *      '/net-worth/men/24-years-old'    → '/net-worth/men/24-years-old' (unchanged)
 */
function stripLocalePrefixFromPath(pathname) {
  const parts = pathname.split('/');
  // parts[0] is '' for paths starting with '/'
  if (parts[1] && LOCALE_PREFIXES.includes(parts[1])) {
    return '/' + parts.slice(2).join('/');
  }
  return pathname;
}

/**
 * Strip a locale prefix from an array of path segments (result of pathname.split('/')).
 * Returns { stripped, prefix } where prefix is e.g. '/de' or '' if none.
 */
function stripLocalePrefixFromParts(parts) {
  // Handles both ['', 'de', 'net-worth', ...] and ['de', 'net-worth', ...] (filter(Boolean) style)
  const firstNonEmpty = parts[0] === '' ? parts[1] : parts[0];
  if (firstNonEmpty && LOCALE_PREFIXES.includes(firstNonEmpty)) {
    if (parts[0] === '') {
      return { stripped: ['', ...parts.slice(2)], prefix: '/' + firstNonEmpty };
    }
    return { stripped: parts.slice(1), prefix: '/' + firstNonEmpty };
  }
  return { stripped: parts, prefix: '' };
}

let hashOfLMMetaData = {};

// const getGeneratorInfo = function (url) {
//   return fetch(urlBase + '/api/v1/generators/fetch_by_url?url=' + encodeURIComponent(url));
// };

const getGeneratorInfo = function (url, locale) {
  const pathArray = url.split('/').filter(Boolean);
  console.log(pathArray, 'pathArray in getGeneratorInfo');

  let apiUrl = '';

  if (pathArray.length > 2) {
    apiUrl = `${urlBase}/api/v1/generators/fetch_by_url?url=${encodeURIComponent(url)}`;
    console.log('Using base generator API for short URL:', apiUrl);
  } else {
    apiUrl = `${urlBase}/api/v1/intermediary/fetch_by_url?url=${encodeURIComponent(url)}`;
    console.log('Using intermediary API for deeper URL:', apiUrl);
  }

  if (locale) {
    apiUrl += `&locale=${encodeURIComponent(locale)}`;
  }

  return fetch(apiUrl);
};

function seo(url) {
  // Extract locale before stripping the prefix
  const locale = getLocaleFromPath(url);

  // Strip locale prefix so CMS API receives the canonical path
  // e.g. '/de/net-worth/men/24-years-old' → '/net-worth/men/24-years-old'
  const strippedUrl = stripLocalePrefixFromPath(url);

  hashOfLMMetaData = {};
  hashOfLMMetaData = returnPageVariableHash(strippedUrl, locale);

  // Retrive PageTitle and MetaDescription of the page. TODO: Rest Hash variables
  getGeneratorInfo(strippedUrl, locale)
    .then((response) => response.json())
    .then((data) => {
      if (data.generator) {
        hashOfLMMetaData.pageTitle = data.generator.title;
        hashOfLMMetaData.pageMetaDescription = data.generator.description;
        hashOfLMMetaData.pageMetaContent = data.generator.content;
        hashOfLMMetaData.pageCanonicalUrl = data.generator.canonical;
        hashOfLMMetaData.previous = data.generator.previous;
        hashOfLMMetaData.next = data.generator.next;

        document.title = hashOfLMMetaData.pageTitle;
        const titleMetaTag = document.querySelector('meta[name=title]');
        const descriptionMetaTag = document.querySelector('meta[name=description]');

        if (titleMetaTag) {
          titleMetaTag.setAttribute('content', hashOfLMMetaData.pageTitle);
        }
        if (descriptionMetaTag) {
          descriptionMetaTag.setAttribute('content', hashOfLMMetaData.pageMetaDescription);
        }

        // Canonical, og:url, prev/next links are handled by SSR — do not overwrite client-side.
        // The CMS returns production URLs without locale prefixes, which would clobber
        // the correct locale-aware canonical set during server rendering.

        const properTitleQuery = document.querySelector('meta[property="og:title"]');
        if (properTitleQuery) {
          properTitleQuery.setAttribute('content', hashOfLMMetaData.pageTitle);
        }
        const properDescriptionQuery = document.querySelector('meta[property="og:description"]');
        if (properDescriptionQuery) {
          properDescriptionQuery.setAttribute('content', hashOfLMMetaData.pageMetaDescription);
        }
        // // Set or update robots meta tag for noindex, nofollow
        // let robotsMetaTag = document.querySelector('meta[name="robots"]');
        // if (!robotsMetaTag) {
        //   robotsMetaTag = document.createElement('meta');
        //   robotsMetaTag.setAttribute('name', 'robots');
        //   document.head.appendChild(robotsMetaTag);
        // }
        // robotsMetaTag.setAttribute('content', 'noindex, nofollow');
      }
    })
    .catch((err) => {
      // throw new Error('Cannot set property of null');
      console.log(err.message);
    });
}

// utils/seo.js

export async function fetchSeoData(url, locale) {
  let apiUrl = urlBase + '/api/v1/generators/fetch_by_url?url=' + encodeURIComponent(url);
  if (locale) {
    apiUrl += '&locale=' + encodeURIComponent(locale);
  }
  const response = await fetch(apiUrl);
  const data = await response.json();
  return data.generator;
}

function generateAlternativeGenderUrl(hashArray = null) {
  const genderMap = {
    men: 'women',
    women: 'men',
    boys: 'girls',
    girls: 'boys',
    household: 'household',
  };

  // If hashArray is provided, use it (for server-side rendering)
  if (hashArray && hashArray.length >= 3) {
    // Strip any locale prefix before index-based gender detection
    const { stripped } = stripLocalePrefixFromParts(hashArray);

    // Handle case where stripped might have empty first element
    // e.g., ['', 'net-worth', 'men', '24-years-old'] or ['net-worth', 'men', '24-years-old']
    let genderIndex = 1;
    if (stripped[0] === '') {
      genderIndex = 2; // Gender is at index 2 if first element is empty
    }

    const currentGender = stripped[genderIndex];
    const alternativeGender = genderMap[currentGender] || 'men';

    // Create the alternative URL using the stripped array (no locale prefix)
    const pathParts = [...stripped];
    pathParts[genderIndex] = alternativeGender;

    // Remove empty first element if present to avoid double slashes
    if (pathParts[0] === '') {
      pathParts.shift();
    }

    // Ensure proper URL construction without double slashes
    const adjustedUrlBase = urlBase.endsWith('/') ? urlBase.slice(0, -1) : urlBase;
    return adjustedUrlBase + '/' + pathParts.join('/');
  }

  // Client-side version using window.location
  if (typeof window === 'undefined') return '/'; // SSR safety

  const rawParts = window.location.pathname.split('/').filter(Boolean);
  // Strip locale prefix so gender is always at index 1
  const { stripped: pathParts } = stripLocalePrefixFromParts(rawParts);

  if (pathParts.length < 3) return '/'; // Not enough parts to modify

  // Replace gender segment with its alternative (gender is at index 1)
  pathParts[1] = genderMap[pathParts[1]] || 'men';

  return '/' + pathParts.join('/');
}

function addNoFollowAttr(htmlString = '') {
  const allowedDomains = ['https://nettleworth.com', 'https://nettleworth-stagging-75c7cd09fba7.herokuapp.com'];

  const isAllowed = (href) => {
    try {
      const url = new URL(href);
      return allowedDomains.some((domain) => url.hostname.includes(domain));
    } catch (error) {
      return false;
    }
  };

  return htmlString.replace(/<a\s+([^>]*href="([^"]+)"[^>]*)>/gi, (match, attributes, href) => {
    if (!isAllowed(href)) {
      if (/rel="nofollow"/i.test(attributes)) {
        return `<a ${attributes}>`;
      } else {
        return `<a ${attributes} rel="nofollow">`;
      }
    }
    return match;
  });
}

const chartsSchemaScript = ({
  chartTitle,
  blurbData,
  pageDataH1Title,
  pageDataH1SubTitle,
  unit,
  chartImage,
  splittedURL,
  locale = 'en-US',
}) => {
  const getMetaTitle = () => {
    return [pageDataH1Title, pageDataH1SubTitle].filter(Boolean).join(' ');
  };

  const getMetaDescription = () => {
    return `In most cases, ${parseUrlForGenderWithString(splittedURL, locale)} ${chartTitle} measurements for this age group (${
      blurbData.ageDesc
    }) will lie within the range between ${blurbData.percentile_3 || blurbData.percentile_2} and ${
      ' ' + blurbData.percentile_97 || blurbData.percentile_98
    } ${unit}. The average ${chartTitle} measurement for this age group ${parseUrlForGenderWithString(
      splittedURL,
    )} is ${blurbData.percentile_50} ${unit}, according to the CDC`;
  };

  return (
    <script
      type="application/ld+json"
      id="schema-script"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'MediaObject',
          name: getMetaTitle(),
          description: getMetaDescription(),
          contentUrl: chartImage || '',
          encodingFormat: 'image/png',
        }),
      }}
    />
  );
};

export {
  hashOfLMMetaData,
  seo,
  addNoFollowAttr,
  getGeneratorInfo,
  generateAlternativeGenderUrl,
  chartsSchemaScript,
  stripLocalePrefixFromPath,
  stripLocalePrefixFromParts,
};
